import { Directive, AfterViewChecked, ElementRef, OnDestroy, inject } from '@angular/core';

/**
 * Diese Direktive erweitert einen PrimeNG <p-dialog>, der eine
 * 'his-search-result'-Komponente enthält.
 *
 * Sie fügt dem Dialog einen zusätzlichen Hyperlink hinzu, mit dem
 * die im Hintergrund ausgeblendete Suchkonfiguration heruntergeladen
 * werden kann. Dabei wird der Klick auf den sichtbaren Link intern
 * an den ursprünglichen, versteckten Hyperlink im Hintergrund delegiert.
 * 
 * Siehe Anfrage 355805
 */

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'p-dialog',
    standalone: false, })

export class HisDialogDirective implements AfterViewChecked, OnDestroy {
    private host = inject<ElementRef<HTMLElement>>(ElementRef);
    private observer?: MutationObserver;
    private observing = false;
    private searchConfigurationLink: HTMLAnchorElement | null = null;
    
    ngAfterViewChecked(): void {
        
        // Link bereits vorhanden: Performance verbessern
        if (this.searchConfigurationLink) {
            return;
        }
        
        const dialogContentElement = this.host.nativeElement.querySelector('.p-dialog-content');
        if (!dialogContentElement) return;

        const genericSearchForm = dialogContentElement.querySelector('his-search-result');
        if (!genericSearchForm) return;

        let searchConfigurationLinkInOverlay = dialogContentElement.querySelector('[data-injected="dialog-search-config-link"]') as HTMLDivElement | null;
        this.searchConfigurationLink = document.querySelector('[data-role="search-config-link"]') as HTMLAnchorElement | null;
        
        if (!searchConfigurationLinkInOverlay && this.searchConfigurationLink) {
            searchConfigurationLinkInOverlay = document.createElement('div');
            searchConfigurationLinkInOverlay.setAttribute('data-injected', 'dialog-search-config-link');
            searchConfigurationLinkInOverlay.classList.add('pageOptionPanelContainer');

            const strong = document.createElement('strong');
            strong.textContent = 'Suchkonfiguration: ';
            searchConfigurationLinkInOverlay.appendChild(strong);

            const a = document.createElement('a');
            a.href = '#';
            a.setAttribute('data-role', 'search-config-link_copy');
            a.style.cursor = 'pointer';
            searchConfigurationLinkInOverlay.appendChild(a);

            dialogContentElement.prepend(searchConfigurationLinkInOverlay);

            a.addEventListener('click', (e) => {
                e.preventDefault();
                this.searchConfigurationLink.click();
            });

            // Initialize observer only once
            if (!this.observing) {
                this.observeOriginalLink(this.searchConfigurationLink, a);
                this.observing = true;
            }

            // Set initial text
            a.textContent = this.searchConfigurationLink.textContent?.trim() ?? '';
        }
    }

    /**
     * Überwacht die Beschriftung des Hyperlinks, damit diese ebenfalls aktualisiert wird,
     * wenn sich der Text des Original-Links ändert.
     */
    private observeOriginalLink(original: HTMLAnchorElement, copy: HTMLAnchorElement) {
        this.observer = new MutationObserver(() => {
            const newText = original.textContent?.trim() ?? '';
            if (copy.textContent !== newText) {
                copy.textContent = newText;
            }
        });

        this.observer.observe(original, {
            characterData: true,
            childList: true,
            subtree: true,
        });
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
    }
}
